import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function MissionBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.18 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative w-full py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Industrial%20oil%20refinery%20at%20night%20with%20glowing%20flare%20stacks%20and%20illuminated%20towers%2C%20complex%20pipeline%20network%20lit%20by%20amber%20industrial%20lights%2C%20dramatic%20long%20exposure%20photography%2C%20dark%20moody%20atmosphere%20with%20warm%20orange%20and%20gold%20highlights%2C%20chemical%20plant%20infrastructure%2C%20cinematic%20industrial%20night%20scene&width=1800&height=800&seq=mission-banner-refinery-01&orientation=landscape"
          alt="Refinería industrial Venezuela"
          className="w-full h-full object-cover object-center"
        />
        {/* Double overlay: solid base + directional gradient */}
        <div className="absolute inset-0 bg-foreground-950/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/90 to-foreground-950/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-14 items-start">

          {/* Left: Misión + Visión */}
          <div className={`flex-1 max-w-xl transition-all duration-800 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-accent-500" />
              <span className="text-accent-400 text-xs font-semibold tracking-widest uppercase">
                Nuestra Esencia
              </span>
            </div>

            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white leading-tight mb-8">
              Con propósito{' '}
              <span className="text-accent-400">claro</span>{' '}
              y visión de futuro
            </h2>

            <div className="space-y-6">
              {/* Misión */}
              <div className="relative p-6 rounded-2xl bg-white/10 border border-white/18 backdrop-blur-sm overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full rounded-l-2xl bg-primary-500" />
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/30">
                    <i className="ri-focus-3-line text-primary-400 text-lg"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg">Misión</h3>
                </div>
                <p className="text-foreground-200 text-sm leading-relaxed">
                  Proveer productos químicos de alta calidad para la industria petrolera venezolana,
                  con formulaciones propias que maximizan la eficiencia operativa y reducen los
                  costos de producción, respaldados por soporte técnico especializado.
                </p>
              </div>

              {/* Visión */}
              <div className="relative p-6 rounded-2xl bg-white/10 border border-white/18 backdrop-blur-sm overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full rounded-l-2xl bg-accent-500" />
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500/30">
                    <i className="ri-eye-line text-accent-400 text-lg"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg">Visión</h3>
                </div>
                <p className="text-foreground-200 text-sm leading-relaxed">
                  Ser la empresa química de referencia en Venezuela y Latinoamérica para la
                  industria de hidrocarburos, expandiendo nuestra capacidad de I+D y exportando
                  soluciones técnicas innovadoras a los principales mercados petroleros del continente.
                </p>
              </div>

              {/* Valores badge row */}
              <div className="flex flex-wrap gap-2 pt-2">
                {['Innovación', 'Calidad', 'Confiabilidad', 'Sostenibilidad', 'Experiencia'].map((v) => (
                  <span key={v} className="px-4 py-1.5 rounded-full text-xs font-semibold bg-primary-500/20 border border-primary-500/40 text-primary-300">
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: timeline */}
          <div className={`flex-1 transition-all duration-800 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-px bg-primary-500" />
              <span className="text-primary-400 text-xs font-semibold tracking-widest uppercase">
                Nuestra Historia
              </span>
            </div>

            <div className="relative pl-8">
              {/* Vertical line */}
              <div className="absolute left-3 top-0 bottom-0 w-px bg-white/20" />

              {[
                { year: '1994', title: 'Fundación', desc: 'Poly Alphas Chemicals C.A. nace en Venezuela con el foco en la industria petrolera nacional.', dot: 'bg-accent-500' },
                { year: '2002', title: 'Laboratorio Propio', desc: 'Inauguramos nuestro laboratorio de I+D con equipos HPHT para simulación de condiciones de fondo.', dot: 'bg-primary-500' },
                { year: '2010', title: 'Expansión de Catálogo', desc: 'Lanzamos la línea completa de fluidos de perforación base agua y base aceite, validados en campo.', dot: 'bg-accent-500' },
                { year: '2018', title: 'ALPHA-29 demulsificante', desc: 'Desarrollamos nuestro producto estrella ALPHA-29, hoy el demulsificante más demandado en Venezuela.', dot: 'bg-primary-500' },
                { year: '2024', title: 'Proyección Internacional', desc: 'Iniciamos exportaciones a mercados de Latinoamérica y el Caribe con nuestra línea de productos certificados.', dot: 'bg-accent-500' },
              ].map((item, i) => (
                <div
                  key={item.year}
                  className={`relative mb-7 last:mb-0 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                  style={{ transitionDelay: `${i * 0.12 + 0.3}s` }}
                >
                  {/* Dot */}
                  <div className={`absolute -left-8 top-1.5 w-6 h-6 rounded-full ${item.dot} flex items-center justify-center border-2 border-foreground-950`}>
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                  <div className="p-4 rounded-xl bg-white/10 border border-white/15 hover:border-white/30 transition-colors">
                    <span className="text-accent-400 text-xs font-bold tracking-widest">{item.year}</span>
                    <h4 className="text-white font-bold text-sm mt-1 mb-1.5">{item.title}</h4>
                    <p className="text-foreground-300 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Bottom CTA bar */}
        <div className={`mt-16 p-8 rounded-2xl bg-white/10 border border-white/18 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <h3 className="text-white font-bold text-xl mb-1">¿Listo para trabajar con nosotros?</h3>
            <p className="text-foreground-300 text-sm">Más de 200 empresas ya confían en nuestras soluciones químicas.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              Contactar Ahora
            </Link>
            <Link
              to="/productos"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/35 text-white hover:bg-white/12 font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-flask-line"></i>
              Ver Productos
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}